import { ActivityLevel, SleepQuality, StressLevel, Gender, BFMethod, TrainingGoal, AdvancedMethodsMyoReps, DedicationLevel, AdvancedMethodsRMTesting } from '../enums';
import { TrainingLevel } from '../data-model/training-level';
import { Equipment } from './equipment';
import { MuscleGrowthPreferenceTable, MuscleGrowthMain } from '../data-model/muscle-growth-preference';
import { SetStats } from './set-stats';
import { uniqueifyName } from '../shared-util';
import { DateTime } from 'luxon';
import { PouchDoc } from './pouch';
import { FitnessPlan, IntensityLevel, Plan_SubSlot } from './fitness-plan';
import { TrainingSplitMode } from './training-split';
import { RestTimerStart } from './rest-timer';

export interface ChangelogEntry {
  version: string;
  changes: string[];
}

export interface ProfileState extends PouchDoc {
  lastAppChangelog?: string;
  lastTodoActions: string[];
}

export interface DebugState {
  _id: string;
  fakeNow: string /** ISO UTC used to fake time passing in test mode */;
}

export enum LongerFinger {
  Index = 1,
  Same = 2, // for people who are unsure
  Ring = 3,
}
export interface ImmutableState {
  _id: string;
  gender: Gender;
  born: string;
  height_cm: number;
  height_unit: string;
  longerFinger?: LongerFinger;
  ankle_cm: number;
  ankle_unit: string;
  wrist_cm: number;
  wrist_unit: string;
}

export interface BFData_Shared {
  method: BFMethod;
  bf_pc?: number;
}

export interface BFData_Self extends BFData_Shared {
  method: BFMethod.Self;
  bfSelf_pc?: number;
}

export interface BFData_Caliper extends BFData_Shared {
  method: BFMethod.Caliper;
  chest_mm: number[];
  suprailiac_mm: number[];
  umbilical_mm: number[];
  thigh_mm: number[];
  tricep_mm: number[];
}

export interface BFData_Tape extends BFData_Shared {
  method: BFMethod.Tape;
  neck_cm?: number; // both men and women
  abdomen_cm?: number; // men only
  waist_cm?: number; // women only
  hip_cm?: number; // women only
}

export type BFData = BFData_Caliper | BFData_Tape | BFData_Self;

export interface DeclaredStrength_Slot {
  idSlot: string;
  // which exercise was used to test
  idExercise?: string;
  stats: SetStats;
  /** which level was calculated based on the stats */
  trainingLevel?: TrainingLevel;
}

export interface DeclaredStrength {
  hasPreviousPerformance?: boolean | 'unknown' /** User can state that he is novice and can't provide lifts, or that they don't remember. */;
  trainingLevelAverage?: TrainingLevel;
  slots: DeclaredStrength_Slot[];
}

export enum NutritionCoachingType {
  Default = 'default',
  None = 'none',
};

export enum NutritionTrackingType {
  Default = 'default',
  None = 'none',
};

export enum MacroTargetsType {
  Exact = 'exact',
  Flexible = 'flexible',
};

export interface DeclaredState {
  _id: string;
  declaredStrength: DeclaredStrength;
  activityLevel: ActivityLevel;
  sleepQuality: SleepQuality;
  stressLevel: StressLevel;
  intakeDeltaPercent?: number;
  trainingVolumeWeeklyAvg?: number;
  trainingGoalGrowth: boolean | TrainingGoal; // true for muscle growth, false for fat loss
  targetBodyfat_pc?: number; // User's target body fat percentage (for FatLoss goal)
  targetBodyWeight_kg?: number; // User's target weight (for WeightLoss/WeightGain goals)
  muscleGrowthPreferenceMain: MuscleGrowthMain;
  muscleGrowthPreferenceTable: MuscleGrowthPreferenceTable;
  advancedMethodsMyoReps: AdvancedMethodsMyoReps;
  advancedMethodsRMTesting: AdvancedMethodsRMTesting;
  advancedMethodsCalorieCycling: boolean;
  morningAppetite: boolean;
  mealFrequency: number; // number of meals per day
  nutritionCoaching?: NutritionCoachingType;
  nutritionTracking?: NutritionTrackingType;
  macroTargets?: MacroTargetsType;
  trainingDedicationLevel?: DedicationLevel;
  nutritionDedicationLevel?: DedicationLevel;
  trainingDays: boolean[]; // 7 days, ISO weekday order (Mon=0, Sun=6), true when training
  wakeupTime: string; // ISO format
  bedTime: string; // ISO format
  sleepHours?: number;
  // Kept only for older app versions, which read it as the rest timer's
  // master switch (missing ≡ off). This app writes it together with
  // restTimerStart through applyRestTimerStart, and reads it only through
  // resolveRestTimerStart (shared/src/data-model/rest-timer.ts).
  restTimerEnabled?: boolean;
  // Whether the rest timer starts by itself when a set is logged (#6014).
  // Missing ≡ derived from restTimerEnabled. Read it ONLY through
  // resolveRestTimerStart and write it ONLY through applyRestTimerStart.
  restTimerStart?: RestTimerStart;
  // Global default rest seconds between sets. Missing ≡ 120. Validated
  // to multiples of 15 in [15, 585] — see shared/src/data-model/rest-timer.ts
  defaultRestSeconds?: number;
  // Which shape of training program the user asked for (#4591). Missing ≡
  // TrainingSplitMode.Optimal, which is what the app has always built, so
  // every existing user is unaffected. Read it ONLY through
  // resolveTrainingSplitMode in shared/src/data-model/training-split.ts.
  trainingSplitMode?: TrainingSplitMode;
}

export function usesCalorieCycling(declaredState: DeclaredState): boolean {
  return declaredState && (declaredState.advancedMethodsCalorieCycling === undefined || declaredState.advancedMethodsCalorieCycling);
}

export function getNutritionOptionsDescription(opts: { nutritionCoaching: boolean, nutritionTracking: boolean, macroTargetsFlexible: boolean }): string {
  if (opts.nutritionCoaching) {
    let targetsText = opts.macroTargetsFlexible ? 'a desired range for macros' : 'exact target number for each macro';
    if (opts.nutritionTracking) {
      return `We suggest ${targetsText} for each meal, and you use this app to plan your meals and track your foods.`;
    } else {
      return `We suggest ${targetsText} for each meal, but you track your actual intake elsewhere - on paper or in another app.`;
    }
  } else {
    if (opts.nutritionTracking) {
      return "You use this app to plan your meals and track your foods, but you don't want the app to control your targets.";
    } else {
      return 'You are handling your own nutrition, and you need this app only for workouts.';
    }
  }
}

export enum MyoRepsMode {
  Avoid = 'avoid',
  Prefer = 'prefer',
  Auto = 'auto',
};

// current stats about one exercise for this user
export interface ExerciseStat {
  /** "Switching" is when the user manually avoids an exercise (lack of equipment...), or if he is too weak for it (e.g. chin-ups).
   * For those cases, we try not to suggest that exercise for the next week or so (just so we don't keep re-suggesting the same thing for different slots/workouts), but we will readily re-suggest soon after when some other exercise plateaus, or is switched.
   */
  switched_utc?: string;
  /** Quarantined exercises, unlike "switched" is a more permanent state, generated when the app determines that the exercise is not good for the user at the moment (it plateaued, or the user is too weak for it, eg). We try to avoid quarantined exercises for much longer.
   */
  quarantined_utc?: string;
  quarantineReason?: 'plateau' | 'weakness';

  estimated1RM_net_kg?: number;
  // the date when this was estimated
  estimated1RM_timestamp: string;
  // OBSOLETE! use equipmentId instead
  weightConfigId?: string;
  // refers to one of the configs in FitnessState.allEquipment
  equipmentId?: string;
  // Equipment IDs for specific units (keeps separate equipment for each unit)
  equipmentIdByUnit?: {
    kg?: string;
    lb?: string;
  };
  /** Custom weight increment to use when equipment is unbrowsed (user lifted unsupported weight) */
  customWeightIncrement_kg?: number;
  /** Custom weight increments for specific units (keeps separate increments for each unit) */
  customWeightIncrementByUnit?: {
    kg?: number;
    lb?: number;
  };
  // marks whether the user saw the tutorial for this exercise
  tutorialSeen?: boolean;
  // unit last used for weight by the user when doing this exercise
  weight_unit: string;
  preferMyoReps?: MyoRepsMode;
  preferRMTesting?: AdvancedMethodsRMTesting;
  // User's stated rep-target preference per intensity tier. Set only when the user explicitly
  // types a value in Edit Workouts → exercise-configuration. Undefined entries fall back to
  // getRepTargetForIntensity defaults at path-rebuild time. Survives plan rebuilds, intensity
  // changes, and exercise swaps (symmetric with preferMyoReps / preferRMTesting / restSeconds).
  // Keys are IntensityLevel enum values; round-trip through CouchDB JSON renders them as strings,
  // so iteration code must Number(key) before comparing to IntensityLevel.X.
  preferRepTargetByIntensity?: Partial<Record<IntensityLevel, number>>;
  // Per-exercise rest-timer override in seconds. Missing ≡ use the
  // global DeclaredState.defaultRestSeconds. Validated to multiples of
  // 15 in [15, 585] — see shared/src/data-model/rest-timer.ts
  restSeconds?: number;

  /// Optional notes entered by the user
  userNotes?: string;
}

export type ExerciseStatState = 'active' | 'plateaued' | 'unsuitable' | 'avoided' | 'ready' | 'unused';

/** how long an exercise stays set aside (quarantined) after a plateau or a weakness result */
export const QUARANTINE_DAYS = 90;

export namespace ExerciseStat {
  export function markSwitched(stat: ExerciseStat, now_utc: DateTime) {
    stat.switched_utc = now_utc.toISO();
  }
  export function markQuarantined(stat: ExerciseStat, now_utc: DateTime, reason: 'plateau' | 'weakness') {
    stat.quarantined_utc = now_utc.toISO();
    stat.quarantineReason = reason;
  }
  /** true when a quarantine is recorded on the stat at all, however old; a damaged value reads as none */
  export function hasQuarantineRecorded(stat: ExerciseStat | null | undefined): boolean {
    return typeof stat?.quarantined_utc === 'string' && stat.quarantined_utc !== '';
  }
  export function unmarkQuarantined(stat: ExerciseStat) {
    stat.quarantined_utc = undefined;
    stat.quarantineReason = undefined;
  }
  export function unmarkSwitched(stat: ExerciseStat) {
    stat.switched_utc = undefined;
  }
  export function isSwitched(stat: ExerciseStat | null, now_utc: DateTime): boolean {
    return stat && stat.switched_utc && now_utc.diff(DateTime.fromISO(stat.switched_utc)).as('days') < 7;
  }
  export function isQuarantined(stat: ExerciseStat | null, now_utc: DateTime): boolean {
    return stat && stat.quarantined_utc && now_utc.diff(DateTime.fromISO(stat.quarantined_utc)).as('days') < QUARANTINE_DAYS;
  }

  export function getExerciseState(
    exerciseId: string,
    now_utc: DateTime,
    plan: FitnessPlan,
    state: FitnessState,
  ): { state: ExerciseStatState; lastUsed_utc?: string } {
    let active = false;
    for (let s of Object.keys(plan.subSlots)) {
      if (plan.subSlots[s].exercise_id == exerciseId) {
        active = true;
        break;
      }
    }
    if (active) {
      return { state: 'active' };
    }

    // NOTE: exercise that has been added to the slots, but doesn't have a stat yet (before first workout) should be `active`, not `unused`
    // there fore this test must come after active
    let stat = state.exerciseStats[exerciseId];
    if (!stat) {
      return { state: 'unused' };
    }

    if (ExerciseStat.isQuarantined(stat, now_utc)) {
      if (stat.quarantineReason === 'plateau') {
        return { state: 'plateaued', lastUsed_utc: stat.quarantined_utc };
      } else {
        console.assert(stat.quarantineReason === 'weakness');
        return { state: 'unsuitable', lastUsed_utc: stat.quarantined_utc };
      }
    }

    if (ExerciseStat.isSwitched(stat, now_utc)) {
      return { state: 'avoided', lastUsed_utc: stat.switched_utc };
    }
    // this logic relies on the fact that ISO UTC strings are sorted in correct chronological order
    let lastUsed_utc = stat.switched_utc;
    if (!lastUsed_utc || lastUsed_utc < stat.quarantined_utc) {
      lastUsed_utc = stat.quarantined_utc;
    }
    return { state: 'ready', lastUsed_utc };
  }
}

/** Badge colour per exercise state. This is presentation, but it is not TEXT, so it stays here. */
export const exerciseStatStateBadgeColors: { [state in ExerciseStatState]: string } = {
  active: 'green',
  plateaued: 'yellow',
  unsuitable: 'magenta',
  avoided: 'gray',
  ready: 'blue',
  unused: 'gray',
};

/**
 * The two pieces of display text an exercise-state badge needs, for ONE state.
 *
 * Shared code cannot import the client's `LL` translation instance, so the
 * caller hands the words in instead — the same way `relativeDayLabel` in
 * shared/src/time.ts takes its three near-day words as a parameter.
 *
 * `T` is what makes the CALLER's string type survive the trip. The client
 * passes `LL.exerciseStates.<state>`, whose members return the branded
 * `TranslatedString`, so `T` infers as that brand and
 * `exerciseStatStateDescription` hands the brand straight back — no cast at
 * the call site, and a caller who passes raw English gets plain `string`,
 * which will not satisfy a `TranslatedString` slot. A unit test (or any
 * non-client caller) can pass plain strings and simply gets `string` back.
 */
export interface ExerciseStatStateTexts<T extends string = string> {
  title: () => T;
  description: () => T;
}

export interface ExerciseStatStateDescription<T extends string = string> {
  /** The state this describes. Compare against THIS, never against `title` — see below. */
  state: ExerciseStatState;
  badgeColor: string;
  title: T;
  description: T;
}

/**
 * Build the badge description for one exercise state.
 *
 * Callers that need to branch on which state this is MUST test `state`, not
 * `title`. `title` is translated text, so comparing it to an English literal
 * silently changes behaviour for every non-English user (that bug was real:
 * both exercise-switch call sites used to test `title !== 'Unused'`).
 */
export function exerciseStatStateDescription<T extends string>(
  state: ExerciseStatState,
  texts: { [state in ExerciseStatState]: ExerciseStatStateTexts<T> },
): ExerciseStatStateDescription<T> {
  return {
    state,
    badgeColor: exerciseStatStateBadgeColors[state],
    title: texts[state].title(),
    description: texts[state].description(),
  };
}

export interface TempSwapSetRecord {
  weight_kg: number;
  reps: number;
  weight_unit: string;
}

export interface TempSwapRecord {
  lastSets: TempSwapSetRecord[];
  lastPerformed_utc: string;
}

export interface FitnessState extends PouchDoc {
  weight_kg: number;
  bfData: BFData;
  lastBfDataUpdate_utc?: string; // ISO UTC date of last update
  lastBfDataWeight_kg?: number; // estimated weight at time of last BF measurement
  exerciseStats: {
    [exercise_id: string]: ExerciseStat;
  };
  allEquipment: {
    [equipment_id: string]: Equipment;
  };
  weightConfigs?: {  // OBSOLETE! use allEquipment instead
    [config_id: string]: Equipment;
  };
  // when an exercise gets swapped out in the plan, the last state is stored here, so it can be reused if the user returns to it immediately
  oldSubslots?: Plan_SubSlot[];
  // stores last performance per exercise for temporary swaps (keyed by exercise_id)
  tempSwapHistory?: { [exercise_id: string]: TempSwapRecord };
}

export function isUsersTrainingGoalGrowth(trainingGoalGrowth: TrainingGoal | boolean, gender: Gender, bf_pc: number): boolean {
  if (typeof trainingGoalGrowth === 'boolean') {
    return trainingGoalGrowth;
  } else {
    switch (trainingGoalGrowth) {
      case TrainingGoal.MuscleGrowth:
        return true;
      case TrainingGoal.FatLoss:
        return false;
      case TrainingGoal.Automatic:
      default:
        if ((gender == Gender.male && bf_pc < 10) || (gender == Gender.female && bf_pc < 20)) {
          return true;
        }
        return false;
    }
  }
}

export function isRecommendedTrainingGoalGrowth(bf_pc: number, gender: Gender, currentTrainingGoalGrowth: TrainingGoal | boolean): boolean {
  const upLimitBF_pc = gender === Gender.male ? 15 : 24;
  const downLimitBF_pc = gender === Gender.male ? 9 : 14;
  if (bf_pc > upLimitBF_pc) {
    return false;
  } else if (bf_pc < downLimitBF_pc) {
    return true;
  } else {
    // if within the limits for hysteresis, recommend the user keeps doing what they are doing now
    return isUsersTrainingGoalGrowth(currentTrainingGoalGrowth, gender, bf_pc);
  }
}

/**
 * The goal the app recommends switching to, or undefined when the user's saved goal already
 * gives them the recommended one at this body fat. Drives the note above the Primary Goal
 * options when a user edits their program (#6291).
 */
export function getRecommendedGoalSwitch(
  bf_pc: number,
  gender: Gender,
  savedTrainingGoalGrowth: TrainingGoal | boolean,
): TrainingGoal.MuscleGrowth | TrainingGoal.FatLoss | undefined {
  const recommendsGrowth = isRecommendedTrainingGoalGrowth(bf_pc, gender, savedTrainingGoalGrowth);
  // resolve the saved goal at the same body fat, so "Decide for me" counts as whatever the app
  // already gives the user, not as fat loss
  const savedIsGrowth = isUsersTrainingGoalGrowth(savedTrainingGoalGrowth, gender, bf_pc);
  if (recommendsGrowth === savedIsGrowth) {
    return undefined;
  }
  return recommendsGrowth ? TrainingGoal.MuscleGrowth : TrainingGoal.FatLoss;
}

export function uniqueifyEquipmentName(fitnessState: FitnessState, currentName: string): string {
  fitnessState.allEquipment = fitnessState.allEquipment || {};
  let usedNames = Object.keys(fitnessState.allEquipment).map(key => fitnessState.allEquipment[key].name);
  return uniqueifyName(usedNames, currentName);
}

export function getDedicationLevelText(dedicationLevel: DedicationLevel): string {
  switch (dedicationLevel) {
    case DedicationLevel.SlowAndSteady:
      return 'Slow and steady';
    case DedicationLevel.Balanced:
      return 'Balanced';
    case DedicationLevel.MaximumEffort:
      return 'Maximum effort';
    default:
      return 'Unknown';
  }
}

// Calculate number of workouts per week from decalared state
export function getWorkoutsPerWeek(declaredState: DeclaredState): number {
  if (!declaredState || !declaredState.trainingDays) {
    return 0;
  }
  return declaredState.trainingDays.filter(day => day).length;
}
